import { useSelector, useDispatch } from 'react-redux';
import FJModal from '../../FJModal';
import useLocalStore from '../../../../hooks/useLocalStore';
import FJDetailsPreviewModal from '../view/FJDetailsPreviewModal.jsx';
import { useCallback, useEffect, useMemo } from 'react';
import FJActionModal from '../../../../redux/action/FJActionModal.js';
import FJUtil from '../../../../util/FJUtil.js';
import FJRequirementsUtil from '../../../../util/FJRequirementsUtil.js';
import FJConstant from '../../../../constants/FJConstant.js';
import FJActionCommon from '../../../../redux/action/FJActionCommon.js';
import FJActionCommunity from '../../../../redux/action/FJActionCommunity.js';
import FJDropDownManager from '../../../../common/js/FJDropDownManager.js';
import FJKeymap from '../../../../util/FJKeymap.js';
import FJAIPhotoEditorConfig from '../../../../config/appConfig/FJAIPhotoEditorConfig.js';
import FJTextToImageConfig from '../../../../config/appConfig/FJTextToImageConfig.js';
import FJImageToImageConfig from '../../../../config/appConfig/FJImageToImageConfig.js';
import FJAppConstant from '../../../../constants/FJAppConstant.js';

const FJDetailsPreviewModalContainer = () => {
    useLocalStore();
    const dispatch = useDispatch();
    const detailsPreviewModalData = useSelector(state => state.modals.detailsPreviewModalData);
    const historyList = useSelector(state => state.common.historyList);
    const sortType = useSelector(state => state.AIVideoGenerator.sortType);
    const appType = useSelector(state => state.common.appType);
    const { display, data = {} } = detailsPreviewModalData;
    const { id, type, jobType, previewList: previewListData } = data;
    const creationList = useSelector(state => {
        if (jobType === FJAIPhotoEditorConfig.historyListType) {
            return state.aiPhotoEditor.creationList;
        } else if (jobType === FJTextToImageConfig.historyListType) {
            return state.textToImage.creationList;
        } else if (jobType === FJImageToImageConfig.historyListType) {
            return state.imageToImage.creationList;
        } else {
            return state.common.creationList;
        }
    });

    const _isVideoGeneration = num => {
        return [28, 14].includes(num);
    };

    const isVideoGeneration = _isVideoGeneration(jobType);

    const _closeModal = useCallback(() => {
        dispatch(FJActionModal.hideDetailsPreviewModalDataAction());
        dispatch(FJActionModal.hideReportModalAction());
        dispatch(FJActionModal.hideWarningModalAction());
        FJDropDownManager.hideAll();
    }, [dispatch]);

    useEffect(() => {
        const unKeyMap1 = FJKeymap.keymap(
            27,
            () => {
                _closeModal();
            },
            {
                scope: 'modal',
            },
        );
        return () => {
            unKeyMap1();
        };
    }, [_closeModal]);

    useEffect(() => {
        if (FJUtil.isAiVideoGeneratorLandingPage()) {
            const dom = document.getElementById('ai-tool-banner-container');
            if (display) {
                dom && (dom.style.zIndex = 9);
            } else if (!display) {
                dom && (dom.style.zIndex = '');
            }
        }
    }, [display]);

    const previewList = useMemo(() => {
        if (previewListData) return previewListData;
        if (type === FJConstant.HISTORY) {
            if (isVideoGeneration) {
                return FJRequirementsUtil.getSortHistoryList(historyList, sortType);
            } else {
                return historyList;
            }
        } else {
            return creationList;
        }
    }, [type, historyList, creationList, isVideoGeneration, sortType, previewListData]);

    const isActiveNext = useMemo(() => {
        if (isVideoGeneration && !previewListData) {
            const currentIndex = previewList.findIndex(item => item.id === id);
            const willPreviewList = previewList.slice(currentIndex + 1);
            return currentIndex < previewList.length - 1 && willPreviewList.some(item => item.status === 'completed');
        } else {
            return previewList.findIndex(item => item.id === id) < previewList.length - 1;
        }
    }, [id, previewList, isVideoGeneration, previewListData]);

    const isActivePrevious = useMemo(() => {
        if (isVideoGeneration && !previewListData) {
            const currentIndex = previewList.findIndex(item => item.id === id);
            const willPreviewList = previewList.slice(0, currentIndex);
            return currentIndex > 0 && willPreviewList.some(item => item.status === 'completed');
        } else {
            return previewList.findIndex(item => item.id === id) > 0;
        }
    }, [id, previewList, isVideoGeneration, previewListData]);

    const previewImage = (data, previewList) => {
        dispatch(
            FJActionModal.showDetailsPreviewModalDataAction({
                data: { ...data, previewList: previewList || previewListData },
            }),
        );
    };

    const changePreviewByIndex = (index, isNext) => {
        const nextIndex = isNext ? index + 1 : index - 1;
        if (previewList[nextIndex]) {
            const item = previewList[nextIndex];
            if (item.status === 'completed') {
                previewImage(item);
            } else {
                changePreviewByIndex(nextIndex, isNext);
            }
        }
    };

    const _switchPreview = (isNext = true) => {
        const index = previewList.findIndex(item => item.id === id);
        const nextIndex = isNext ? index + 1 : index - 1;
        if (previewList[nextIndex]) {
            const item = previewList[nextIndex];

            if (_isVideoGeneration(item.jobType)) {
                if (item.status === 'completed') {
                    previewImage(item);
                } else {
                    changePreviewByIndex(nextIndex, isNext);
                }
            } else {
                previewImage(item);
            }
        }
    };

    const _onDelete = () => {
        FJDropDownManager.hideAll();
        if (data.workType === FJConstant.MY_CREATIONS_DETAIL_WORK || data.workType === FJConstant.MY_CREATIONS_WORK) {
            dispatch(
                FJActionCommunity.multiDeleteAction([data.id], bool => {
                    if (bool) {
                        _closeModal();
                    }
                }),
            );
            return;
        }

        let index = previewList.findIndex(item => item.id === id);
        dispatch(
            FJActionCommon.multiDeleteAction([id], list => {
                if (!list) return;
                if (!list.length) {
                    _closeModal();
                    return;
                }

                if (appType === FJAppConstant.AI_COMMUNITY) {
                    const newList = previewList.filter(item => item.id !== id);
                    if (!newList.length) {
                        _closeModal();
                        return;
                    }
                    index >= newList.length && (index = newList.length - 1);
                    previewImage(newList[index], newList);
                } else if (isVideoGeneration) {
                    const tempList = FJRequirementsUtil.getSortHistoryList(list, sortType);

                    let data = null;
                    for (let i = index; i < tempList.length; i++) {
                        const item = tempList[i];
                        if (item.status === 'completed') {
                            data = item;
                            break;
                        }
                    }
                    if (!data) {
                        for (let i = index; i >= 0; i--) {
                            const item = tempList[i];
                            if (item && item.status === 'completed') {
                                data = item;
                                break;
                            }
                        }
                    }
                    if (data) {
                        previewImage(data);
                    } else {
                        _closeModal();
                    }
                } else {
                    index >= list.length && (index = list.length - 1);
                    previewImage(list[index]);
                }
            }),
        );
    };
    const hidePageBox = useMemo(() => {
        return type === FJConstant.CREATIONS && previewList?.length <= 1;
    }, [type, previewList]);
    return (
        <FJModal visible={display} borderRadius={0}>
            <FJDetailsPreviewModal
                onClose={_closeModal}
                active={display}
                info={data}
                switchPreview={_switchPreview}
                isActiveNext={isActiveNext}
                isActivePrevious={isActivePrevious}
                onDelete={_onDelete}
                hidePageBox={hidePageBox}
            />
        </FJModal>
    );
};
export default FJDetailsPreviewModalContainer;
